import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';

import { Row } from 'components/shared/Layout';
import { ActionButton } from 'components/ui/Button';
import { bookingShape } from 'constants/propTypes';
import { ErrorMessage, ErrorContainer, InputContainer, SelectContainer, StyledInput, StyledLabel } from './SelectPassenger.styles';

const SelectPassengers = ({ booking = {}, toggleGroup = () => () => null, selectedItems = [] }) => {
  const [search, setSearch] = useState('');
  const [hasError, setError] = useState(false);
  const { passengers = [], outbound = [], inbound = [] } = booking;

  const isSelect = selectedItems.length > 0;

  const maxPassengerId = Math.max(...passengers.map(({ id }, index) => `${id}`.split('_')?.[1] || index));

  const handleSelect = () => {
    const range = new Set();

    search.split(',').forEach((r) => {
      const numbers = r.split('-');

      const from = Number(numbers[0] ?? 0);
      const to = Number(numbers[1] ?? 0);

      if (from > 0 && to > 0 && from <= to) {
        Array.from({ length: to - from + 1 }, (_, i) => range.add(i + from));
      } else if (from > 0 && to === 0) {
        range.add(Number(numbers[0]));
      }
    });

    if (Math.max(...range) > maxPassengerId) {
      setError(true);

      return;
    }

    const outboundRefs = [];
    const inboundRefs = [];
    outbound.forEach(({ bookingItems = [] }) =>
      bookingItems.forEach(({ passengerId, itemRef }) => {
        const id = Number(`${passengerId}`.split('_')?.[1] || 0);
        if (range.has(id)) {
          outboundRefs.push(itemRef);
        }
      })
    );
    inbound.forEach(({ bookingItems = [] }) =>
      bookingItems.forEach(({ passengerId, itemRef }) => {
        const id = Number(`${passengerId}`.split('_')?.[1] || 0);
        if (range.has(id)) {
          inboundRefs.push(itemRef);
        }
      })
    );

    toggleGroup([...outboundRefs, ...inboundRefs])();
    setSearch('');
  };

  const handleDeselect = () => toggleGroup(selectedItems)();

  const handleSearchChange = (event) => {
    if (hasError) {
      setError(false);
    }

    if (!event.target.value) {
      setSearch('');
    }

    if (/^(?:,?\d+-\d+|,?\d+|,|-)+$/g.test(event.target.value)) {
      setSearch(event.target.value);
    }
  };

  return (
    <Row span={12} start={3} marginBottom="16px">
      <SelectContainer>
        <InputContainer>
          <StyledLabel>
            <FormattedMessage id="journeySegment.selectPassengers" />
          </StyledLabel>
          <StyledInput
            id="selection-search"
            value={search}
            onChange={handleSearchChange}
            type="text"
            name="selection-search"
            data-cy="selection-search"
            data-testid="selection-search"
            disabled={isSelect}
          />
        </InputContainer>
        <ActionButton
          data-cy="select-passengers"
          disabled={hasError || isSelect ? false : !search}
          onClick={isSelect ? handleDeselect : handleSelect}
        >
          <FormattedMessage id={isSelect ? 'journeySegment.clearSelection' : 'journeySegment.select'} />
        </ActionButton>
      </SelectContainer>
      {hasError ? (
        <ErrorContainer>
          <ErrorMessage data-cy="error-select-passenger">
            <FormattedMessage id="journeySegment.outsideRange" values={{ number: maxPassengerId }} />
          </ErrorMessage>
        </ErrorContainer>
      ) : null}
    </Row>
  );
};

SelectPassengers.propTypes = {
  booking: bookingShape,
  toggleGroup: PropTypes.func,
  selectedItems: PropTypes.arrayOf(PropTypes.string)
};

export default SelectPassengers;
